/* eslint-disable max-lines */
import { useRouter } from 'next/router';
import { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import useHomeFeed, { getKey } from '@api/hooks/browse/useHomeFeed';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { useSWRConfig } from 'swr';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import InfiniteFeedRow from '@cont/SWRInfiniteFeedRow/InfiniteFeedRow';
import styled from '@emotion/styled';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import { mediaQuery } from '@styles/index';
import RenderFeedBlock from './RenderFeedBlock';

const Identity = (feeds) => feeds;

const Loader = () => (
  <ProgressWrapper>
    <CircularLoader />
  </ProgressWrapper>
);

const ProgressWrapper = styled.div`
  grid-column: span 18;
  width: 484px;
  margin: auto;
  height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-column: span 10;
  }
`;

const HomeFeeds = ({ RenderFeedRow, addExtraBlock }) => {
  const router = useRouter();
  const goalUID = router.query.goalUID?.toUpperCase();
  const { goalInfo: goalData } = useGoalInfo(goalUID);
  const goalInfo = useMemo(
    () => ({
      name: goalData.name,
      goalSlug: goalData.goalSlug || goalData.slug,
      slug: goalData.slug,
      icon: goalData.icon,
      uid: goalData.uid,
      isRecordedGoal: goalData.isRecordedGoal
    }),
    [goalData]
  );
  const hasGoalInfo = !!goalInfo;
  const refetchingIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      // Clear timeout to avoid memory leaks.
      if (refetchingIntervalRef.current)
        refetchingIntervalRef.current = clearInterval(
          refetchingIntervalRef.current
        );
    };
  }, []);

  const homeFeedKey = getKey(goalUID);
  const { fallback = {} } = useSWRConfig();
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const {
    homeFeed,
    setSize,
    isValidating,
    mutate: refetchHomeFeed
  } = useHomeFeed(goalUID, {
    fallbackData: fallback[homeFeedKey]
  });

  useEffect(() => {
    if (isLoggedIn) {
      refetchHomeFeed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const loadMoreFeedBlocks = () => {
    if (!isValidating && homeFeed?.next) {
      setSize((nPages) => nPages + 1);
    }
  };

  if (!hasGoalInfo || !homeFeed?.results?.length) return null;

  return (
    <>
      <InfiniteFeedRow
        data={addExtraBlock(homeFeed)?.results}
        isLoading={isValidating}
        renderItem={RenderFeedRow}
        onLoadMore={loadMoreFeedBlocks}
        renderLoader={Loader}
      />
    </>
  );
};

export default HomeFeeds;

HomeFeeds.propTypes = {
  RenderFeedRow: PropTypes.elementType,
  addExtraBlock: PropTypes.func
};

HomeFeeds.defaultProps = {
  RenderFeedRow: RenderFeedBlock,
  addExtraBlock: Identity
};
